import React, { useState, useEffect } from 'react';
import {
    Table, Select, DatePicker, Button, message, Card,
    Typography, Tooltip, Drawer, Tabs, Tag, Badge, Empty, Collapse
} from 'antd';
import { DownloadOutlined, EyeOutlined, ClockCircleOutlined, AppstoreOutlined, CopyOutlined, MessageOutlined } from '@ant-design/icons';
import { useAuthOrg } from '../context/AuthOrgContext';
import coreAPIService from '../apiServices/coreAPIService';
import orgManagementService from '../apiServices/orgManagementService';
import { CTW_API_ENDPOINT, CTW_NODE_API_ENDPOINT } from '../utils';
import Heading from '../common/Heading';
import CommonLayout from '../common/LayoutBlock';
import dayjs from 'dayjs';
import '../assets/css/tablesShadow.css';
import WhatsAppChat, { ChatLogPayload } from '../components/WhatsAppChat';
import { useBreadcrumbs } from '../context/BreadcrumbContext';
import conversationService from '../apiServices/conversationService';

const { Option } = Select;
const { RangePicker } = DatePicker;

interface Milestone {
    milestoneName: string;
    time: string;
}

interface Lead {
    wabaNumber: string;
    wabaName: string;
    username: string;
    userMobile: string;
    userId: string;
    clientUserId?: string | number;
    city: string;
    var: Record<string, any> | null;
    milestones: Record<string, Milestone[]>;
    capiPushed: string;
}

const Leads: React.FC = () => {
    const { availableOrgs, currentOrg } = useAuthOrg();
    const [loading, setLoading] = useState(false);
    const [loadingCampaigns, setLoadingCampaigns] = useState(false);
    const [loadingWaba, setLoadingWaba] = useState(false);
    const [data, setData] = useState<Lead[]>([]);
    const [filters, setFilters] = useState({
        orgId: currentOrg?.orgId || '',
        source: 'Meta' as 'Meta' | 'organic',
        campaignId: undefined as string | undefined,
        milestone: 'LeadSubmitted',
        wabaId: undefined as string | undefined,
        dateRange: [null, null] as [dayjs.Dayjs | null, dayjs.Dayjs | null],
    });
    const [campaigns, setCampaigns] = useState<{ id: string; name: string }[]>([]);
    const [milestones, setMilestones] = useState<{ milestone_name: string; milestone_no: number }[]>([]);
    const [wabaAccounts, setWabaAccounts] = useState<{ id: string; name: string; number: string }[]>([]);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
    });

    // Drawer state
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [drawerRecord, setDrawerRecord] = useState<Lead | null>(null);
    const [drawerTab, setDrawerTab] = useState<'vars' | 'milestones' | 'chat'>('vars');
    const [logs, setLogs] = useState<ChatLogPayload[]>([]);

    const { setBreadcrumbs } = useBreadcrumbs();

    useEffect(() => {
        setBreadcrumbs([
            {
                title: <div className="text-lg cursor-pointer" >Leads</div>
            }
        ]);
    }, []);

    useEffect(() => {
        if (filters.orgId) {
            if (filters.source === 'Meta') {
                fetchCampaigns(filters.orgId);
            } else {
                fetchWabaAccounts(filters.orgId);
            }
        }
    }, [filters.orgId, filters.source]);

    useEffect(() => {
        if (filters.campaignId) {
            fetchMilestones(filters.campaignId);
        } else {
            setMilestones([]);
            setFilters(prev => ({ ...prev, milestone: '' }));
        }
    }, [filters.campaignId]);

    useEffect(() => {
        if (milestones.length > 0) {
            const found = milestones.find(m => m.milestone_name === 'LeadSubmitted');
            if (found) {
                setFilters(prev => ({ ...prev, milestone: found.milestone_name }));
            }
        }
    }, [milestones]);

    useEffect(() => {
        if (filters.orgId && pagination.current) {
            fetchLeads();
        }
    }, [pagination.current]);

    const fetchCampaigns = async (orgId: string) => {
        setLoadingCampaigns(true);
        try {
            const res = await coreAPIService.get<any>(CTW_API_ENDPOINT, `v2/util/panel-campaign-dropdown?orgId=${orgId}`);
            if (res.meta.code === 200) {
                setCampaigns(res.data.filter((c: any) => c.campaignId !== null).map((c: any) => ({ id: c.campaignId, name: c.campaignName })));
            } else {
                setCampaigns([]);
            }
        } catch (error) {
            console.error('Failed to fetch campaigns', error);
            setCampaigns([]);
        } finally {
            setLoadingCampaigns(false);
        }
    };

    const fetchMilestones = async (campaignId: string) => {
        try {
            const res = await coreAPIService.get<any>(CTW_NODE_API_ENDPOINT, `v2/bots/milestones?campaignId=${campaignId}`);
            if (res.meta.code === 200) {
                const filtered = (res.data || []).filter((m: any) =>
                    m.milestone_name === 'LeadSubmitted' || m.milestone_name === 'QualifiedLead'
                );
                setMilestones(filtered);
            } else {
                setMilestones([]);
            }
        } catch (error) {
            console.error('Failed to fetch milestones', error);
            setMilestones([]);
        }
    };

    const fetchWabaAccounts = async (orgId: string) => {
        setLoadingWaba(true);
        try {
            const res = await orgManagementService.getWabaDetails();
            if (res?.meta?.code === 200 && res.data) {
                const orgData = res.data.find((org: any) => org.orgId === orgId);
                if (orgData) {
                    setWabaAccounts(orgData.wabaDetails.map((w: any) => ({
                        id: w.wabaId,
                        name: w.subdomain,
                        number: w.wabaNumber,
                    })));
                } else {
                    setWabaAccounts([]);
                }
            }
        } catch (error) {
            console.error('Failed to fetch WABA accounts', error);
            setWabaAccounts([]);
        } finally {
            setLoadingWaba(false);
        }
    };

    const fetchLeads = async () => {
        if (!filters.orgId || !filters.source) {
            message.warning('Please select Account and Source');
            return;
        }

        setLoading(true);
        try {
            const payload: any = {
                org_id: filters.orgId,
                source: filters.source,
                start_date: filters.dateRange[0]?.format('YYYY-MM-DD'),
                end_date: filters.dateRange[1]?.format('YYYY-MM-DD'),
                limit: pagination.pageSize,
                offset: (pagination.current - 1) * pagination.pageSize,
            };

            if (filters.source === 'Meta') {
                payload.campaign_id = filters.campaignId;
                payload.milestone = filters.milestone || 'LeadSubmitted';
            } else {
                payload.waba_number = filters.wabaId;
            }

            const res = await coreAPIService.post<any>(CTW_API_ENDPOINT, 'v2/bot-analytics/leads/filter', payload);

            if (res?.meta?.code === 200 && res.data) {
                setData(res.data.leads || []);
                // console.log(res, res.data);
                setPagination(prev => ({ ...prev, total: res.data.count || 0 }));
            } else {
                setData([]);
                setPagination(prev => ({ ...prev, total: 0 }));
            }
        } catch (error) {
            message.error('Failed to fetch leads');
            setData([]);
        } finally {
            setLoading(false);
        }
    };

    const handleExport = async () => {
        if (!filters.orgId || !filters.source) {
            message.warning('Please select Account and Source to export');
            return;
        }

        message.loading({ content: 'Exporting data...', key: 'export' });
        try {
            const payload: any = {
                org_id: filters.orgId,
                source: filters.source,
                start_date: filters.dateRange[0]?.format('YYYY-MM-DD'),
                end_date: filters.dateRange[1]?.format('YYYY-MM-DD'),
            };

            if (filters.source === 'Meta') {
                payload.campaign_id = filters.campaignId;
                payload.milestone = filters.milestone;
            } else {
                payload.waba_number = filters.wabaId;
            }

            const res = await coreAPIService.post<any>(CTW_API_ENDPOINT, 'v2/bot-analytics/leads/export', payload);
            if (res?.data?.fileContent) {
                const { fileContent, contentType } = res.data;
                const byteCharacters = atob(fileContent);
                const byteNumbers = new Array(byteCharacters.length);
                for (let i = 0; i < byteCharacters.length; i++) {
                    byteNumbers[i] = byteCharacters.charCodeAt(i);
                }
                const byteArray = new Uint8Array(byteNumbers);
                const csvText = new TextDecoder().decode(byteArray);
                const parseCsv = (csv: string): string[][] => {
                    const rows: string[][] = [];
                    let row: string[] = [];
                    let value = '';
                    let inQuotes = false;

                    for (let i = 0; i < csv.length; i += 1) {
                        const character = csv[i];
                        const nextCharacter = csv[i + 1];

                        if (character === '"' && inQuotes && nextCharacter === '"') {
                            value += '"';
                            i += 1;
                        } else if (character === '"') {
                            inQuotes = !inQuotes;
                        } else if (character === ',' && !inQuotes) {
                            row.push(value);
                            value = '';
                        } else if ((character === '\n' || character === '\r') && !inQuotes) {
                            if (character === '\r' && nextCharacter === '\n') i += 1;
                            row.push(value);
                            if (row.some((cell) => cell !== '')) rows.push(row);
                            row = [];
                            value = '';
                        } else {
                            value += character;
                        }
                    }

                    if (value || row.length) {
                        row.push(value);
                        if (row.some((cell) => cell !== '')) rows.push(row);
                    }

                    return rows;
                };
                const escapeCsvValue = (value: string) => {
                    if (/[",\n\r]/.test(value)) return `"${value.replace(/"/g, '""')}"`;
                    return value;
                };
                const normalizeHeader = (header: string) => header.replace(/^\uFEFF/, '').toLowerCase().replace(/[^a-z0-9]/g, '');
                const csvRows = parseCsv(csvText);
                const csvHeaders = csvRows[0] || [];
                const headerIndexes = new Map(
                    csvHeaders.map((header, index) => [normalizeHeader(header), index])
                );
                const exportColumns = [
                    { title: 'WA Name', aliases: ['waname', 'username'] },
                    { title: 'WA Number', aliases: ['wanumber', 'usermobile', 'user_mobile', 'userid', 'user_id'] },
                    { title: 'Name', aliases: ['name', 'wabaname'] },
                    { title: 'Mobile', aliases: ['mobile', 'wabanumber', 'waba_number'] },
                    { title: 'City', aliases: ['city'] },
                    { title: 'Latest Milestone', aliases: ['latestmilestone', 'milestone', 'milestonename', 'milestone_name'] },
                    { title: 'Event Time', aliases: ['eventtime', 'updatedat', 'event_time', 'latestmilestonetime', 'milestonetime'] },
                    { title: 'Var Object', aliases: ['var', 'variables', 'varobject'] },
                    { title: 'CAPI status (Offline)', aliases: ['capistatusoffline', 'capipushed', 'capi_status', 'capi_status_offline'] },
                ];
                const getRawCsvValue = (row: string[], aliases: string[]) => {
                    const index = aliases
                        .map(normalizeHeader)
                        .map((alias) => headerIndexes.get(alias))
                        .find((headerIndex) => headerIndex !== undefined);
                    return index === undefined ? '' : row[index] || '';
                };
                let exportRecords = data;
                try {
                    const detailsRes = await coreAPIService.post<any>(CTW_API_ENDPOINT, 'v2/bot-analytics/leads/filter', {
                        ...payload,
                        limit: 10000000,
                        offset: 0,
                    });
                    if (detailsRes?.meta?.code === 200 && Array.isArray(detailsRes.data?.leads)) {
                        exportRecords = detailsRes.data.leads;
                    }
                } catch (detailsError) {
                    console.warn('Unable to fetch detailed lead records for export', detailsError);
                }
                const tableRecordsByKey = new Map<string, Lead>();
                exportRecords.forEach((record) => {
                    [record.userMobile, record.userId, record.username]
                        .filter(Boolean)
                        .forEach((key) => tableRecordsByKey.set(String(key), record));
                });
                const parseJsonValue = (value: string): any => {
                    if (!value) return null;
                    try {
                        return JSON.parse(value);
                    } catch {
                        return null;
                    }
                };
                const getExportColumnValue = (column: (typeof exportColumns)[number], row: string[]) => {
                    const directValue = getRawCsvValue(row, column.aliases);
                    if (directValue) return directValue;

                    const rowKey = getRawCsvValue(row, ['usermobile', 'user_mobile', 'userid', 'user_id', 'wanumber', 'username', 'waname']);
                    const tableRecord = tableRecordsByKey.get(rowKey);
                    const milestones = parseJsonValue(getRawCsvValue(row, ['milestones'])) || tableRecord?.milestones;
                    if (column.title === 'Latest Milestone' && milestones) {
                        return getLatestMilestone(milestones) || '';
                    }
                    if (column.title === 'Event Time' && milestones) {
                        return getLatestMilestoneTime(milestones) || '';
                    }
                    if (tableRecord) {
                        switch (column.title) {
                            case 'WA Name':
                                return tableRecord.username || '';
                            case 'WA Number':
                                return tableRecord.userMobile || tableRecord.userId || '';
                            case 'Name':
                                return tableRecord.wabaName || '';
                            case 'Mobile':
                                return tableRecord.wabaNumber || '';
                            case 'City':
                                return tableRecord.city || '';
                            case 'Var Object':
                                return tableRecord.var
                                    ? (typeof tableRecord.var === 'string' ? tableRecord.var : JSON.stringify(tableRecord.var))
                                    : '';
                            case 'CAPI status (Offline)':
                                return tableRecord.capiPushed || '';
                            default:
                                return '';
                        }
                    }

                    return '';
                };
                const hasKnownHeader = exportColumns.some((column) => column.aliases
                    .map(normalizeHeader)
                    .some((alias) => headerIndexes.has(alias))) || headerIndexes.has('milestones');
                const orderedCsv = csvRows.length > 0 && hasKnownHeader
                    ? [
                        exportColumns.map((column) => column.title),
                        ...csvRows.slice(1).map((row) => exportColumns.map((column) => getExportColumnValue(column, row))),
                    ].map((row) => row.map(escapeCsvValue).join(','))
                    : [];
                const blob = new Blob([orderedCsv.join('\n') || csvText], { type: contentType || 'text/csv' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `leads_${filters.milestone}_${new Date().getTime()}.csv`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(url);
                message.success('Leads exported successfully');
            } else {
                throw new Error(res?.meta?.message ?? 'Export failed');
            }
        } catch (error) {
            console.error(error);
            message.error({ content: 'Failed to export data', key: 'export' });
        }
    };

    const openDrawer = async (record: Lead, tab: 'vars' | 'milestones' | 'chat') => {
        const clientUserId = record.clientUserId ?? record.userId;
        const chatLogs: { meta: { code: number }, data: ChatLogPayload[] } = await conversationService.getLogging({
            clientUserId,
            orgId: filters.orgId,
            userId: record.userId,
        });
        if (chatLogs.meta.code == 200) setLogs(chatLogs.data);
        setDrawerRecord(record);
        setDrawerTab(tab);
        setDrawerOpen(true);
    };

    /** Find the most recent milestone across all sessions */
    const getLatestMilestone = (milestones: Record<string, Milestone[]>): string | null => {
        if (!milestones) return null;
        const allMilestones = Object.values(milestones).flat();
        if (allMilestones.length === 0) return null;
        return allMilestones.reduce((latest, m) =>
            dayjs(m.time).isAfter(dayjs(latest.time)) ? m : latest
        ).milestoneName;
    };

    const getLatestMilestoneTime = (milestones: Record<string, Milestone[]>): string | null => {
        if (!milestones) return null;
        const allMilestones = Object.values(milestones).flat();
        if (allMilestones.length === 0) return null;
        return dayjs(allMilestones.reduce((latest, m) =>
            dayjs(m.time).isAfter(dayjs(latest.time)) ? m : latest
        ).time).format('YYYY-MM-DD HH:mm:ss');
    };

    const columns = [
        {
            title: 'WA Name',
            dataIndex: 'username',
            key: 'username',
            render: (val: string) => val || '-',
        },
        {
            title: 'WA Number',
            key: 'waNumber',
            render: (_: unknown, record: Lead) => record.userMobile || record.clientUserId || record.userId || '-',
        },
        {
            title: 'Name',
            dataIndex: 'wabaName',
            key: 'wabaName',
            render: (val: string) => val || '-',
        },
        {
            title: 'Mobile',
            dataIndex: 'wabaNumber',
            key: 'wabaNumber',
            render: (val: string) => val || '-',
        },
        {
            title: 'City',
            dataIndex: 'city',
            key: 'city',
            render: (val: string) => val || '-',
        },
        {
            title: 'Latest Milestone',
            key: 'latestMilestone',
            render: (_: any, record: Lead) => {
                const latest = getLatestMilestone(record.milestones);
                return latest ? <Tag color="blue">{latest}</Tag> : <span style={{ color: '#aaa' }}>—</span>;
            },
        },
        {
            title: 'Event Time',
            dataIndex: 'updatedAt',
            key: 'updatedAt',
            render: (_: any, record: Lead) => getLatestMilestoneTime(record.milestones) || '-',
        },
        // {
        //     title: 'Variables',
        //     key: 'vars',
        //     render: (_: any, record: Lead) => {
        //         const count = Object.keys(record.var || {}).length;
        //         return (
        //             <Button
        //                 size="small"
        //                 icon={<AppstoreOutlined />}
        //                 onClick={() => openDrawer(record, 'vars')}
        //                 disabled={count === 0}
        //             >
        //                 {count} var{count !== 1 ? 's' : ''}
        //             </Button>
        //         );
        //     },
        // },
        // {
        //     title: 'Milestones',
        //     key: 'milestones',
        //     render: (_: any, record: Lead) => {
        //         const count = Object.values(record.milestones || {}).flat().length;
        //         return (
        //             <Button
        //                 size="small"
        //                 icon={<ClockCircleOutlined />}
        //                 onClick={() => openDrawer(record, 'milestones')}
        //                 disabled={count === 0}
        //             >
        //                 {count} milestone{count !== 1 ? 's' : ''}
        //             </Button>
        //         );
        //     },
        // },
        {
            title: 'Detail',
            key: 'detail',
            render: (_: any, record: Lead) => (
                <Tooltip title="View Details">
                    <Button
                        size="small"
                        type="link"
                        icon={<EyeOutlined />}
                        onClick={() => openDrawer(record, 'vars')}
                    />
                </Tooltip>
            ),
        },
        {
            title: 'CAPI status (Offline)',
            dataIndex: 'capiPushed',
            key: 'capiPushed',
            render: (val: string) => {
                if (!val) return <span style={{ color: '#aaa' }}>—</span>;
                return (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                        {val.split(',').map(tag => (
                            <Tag key={tag.trim()} color="success" style={{ fontSize: '10px', margin: 0 }}>
                                {tag.trim()}
                            </Tag>
                        ))}
                    </div>
                );
            },
        },
    ];

    // ---- Drawer content ----
    const renderVarsPanel = (record: Lead) => {
        let variables: any = record.var;

        if (typeof variables === 'string') {
            try {
                variables = JSON.parse(variables);
            } catch (error) {
                return (
                    <div style={{ padding: 12 }}>
                        <Empty description="Invalid variable data" />
                        <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', marginTop: 8 }}>
                            {variables}
                        </pre>
                    </div>
                );
            }
        }

        if (!variables || typeof variables !== 'object') {
            return <Empty description="No variables available" />;
        }

        const entries = Object.entries(variables);

        if (!entries.length) {
            return <Empty description="No variables available" />;
        }

        const handleCopy = (value: any) => {
            navigator.clipboard.writeText(
                typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value ?? '')
            );
        };

        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {entries.map(([key, value]) => (
                    <div
                        key={key}
                        style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-start',
                            padding: '12px 14px',
                            background: '#f5f7fa',
                            borderRadius: 8,
                            gap: 12,
                        }}
                    >
                        {/* KEY */}
                        <Typography.Text
                            strong
                            style={{
                                minWidth: 140,
                                color: '#1F344C',
                                wordBreak: 'break-word',
                            }}
                        >
                            {key}
                        </Typography.Text>

                        {/* VALUE */}
                        <div
                            style={{
                                flex: 1,
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 6,
                                alignItems: 'flex-end',
                            }}
                        >
                            {value && typeof value === 'object' ? (
                                <div
                                    className="grid"
                                    style={{
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        gap: 6,
                                        justifyContent: 'flex-end',
                                    }}
                                >
                                    {Object.entries(value).map(([subKey, subVal]) => (
                                        <Tag
                                            key={subKey}
                                            className="bg-white text-xs text-black"
                                            style={{
                                                whiteSpace: 'normal',
                                                wordBreak: 'break-word',
                                                maxWidth: 250,
                                            }}
                                        >
                                            <strong>{subKey}:</strong> {String(subVal ?? '—')}
                                        </Tag>
                                    ))}
                                </div>
                            ) : (
                                <Typography.Text
                                    style={{
                                        whiteSpace: 'pre-wrap',   // preserves line breaks
                                        wordBreak: 'break-word',  // wraps long strings
                                        textAlign: 'right',
                                        maxWidth: 400,
                                    }}
                                >
                                    {String(value ?? '—')}
                                </Typography.Text>
                            )}

                            {/* COPY BUTTON */}
                            {/* <Button
                                size="small"
                                icon={<CopyOutlined />}
                                onClick={() => handleCopy(value)}
                            >
                                Copy
                            </Button> */}
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    const renderMilestonesPanel = (record: Lead) => {
        if (!record.milestones || Object.keys(record.milestones).length === 0) {
            return <Empty description="No milestones available" />;
        }
        const latestName = getLatestMilestone(record.milestones);

        // Sort sessions descending (Session 2, Session 1)
        const sortedSessions = Object.keys(record.milestones).sort((a, b) => {
            const numA = parseInt(a.replace(/\D/g, '')) || 0;
            const numB = parseInt(b.replace(/\D/g, '')) || 0;
            return numB - numA;
        });

        const renderMilestoneRow = (m: Milestone, isOverallLatest: boolean) => (
            <div
                key={`${m.milestoneName}-${m.time}`}
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '10px 14px',
                    background: isOverallLatest ? '#e6f4ff' : '#f5f7fa',
                    borderRadius: 8,
                    border: isOverallLatest ? '1px solid #91caff' : '1px solid transparent',
                }}
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {isOverallLatest && <Badge status="processing" />}
                    <Typography.Text strong style={{ color: isOverallLatest ? '#0958d9' : '#1F344C' }}>
                        {m.milestoneName}
                    </Typography.Text>
                    {isOverallLatest && <Tag color="blue" style={{ marginLeft: 4 }}>Latest</Tag>}
                </div>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    <ClockCircleOutlined style={{ marginRight: 4 }} />
                    {dayjs(m.time).format('YYYY-MM-DD HH:mm:ss')}
                </Typography.Text>
            </div>
        );

        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                {sortedSessions.map((sessionKey, sessionIdx) => {
                    const milestones = record.milestones[sessionKey];
                    const sortedMilestones = [...milestones].sort((a, b) => dayjs(b.time).valueOf() - dayjs(a.time).valueOf());

                    const timeSpentMs = dayjs(sortedMilestones[0]?.time).diff(dayjs(sortedMilestones[sortedMilestones.length - 1]?.time));
                    const timeSpentSec = Math.floor((timeSpentMs || 0) / 1000);
                    const mins = Math.floor(timeSpentSec / 60);
                    const secs = timeSpentSec % 60;
                    const timeSpentStr = mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;

                    return (
                        <div key={sessionKey}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                                <Typography.Text strong style={{ color: '#1F344C', fontSize: 16 }}>
                                    {sessionKey.replace("_", " ").toLocaleUpperCase()}
                                </Typography.Text>
                                <Tag color="cyan" style={{ margin: 0 }}>
                                    <ClockCircleOutlined style={{ marginRight: 4 }} />
                                    Time spent: {timeSpentStr}
                                </Tag>
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                {sortedMilestones.length > 0 && (
                                    <Collapse ghost size="small" style={{ padding: 0 }}>
                                        <Collapse.Panel
                                            header={<Typography.Text type="secondary">View {sortedMilestones.length} milestone{sortedMilestones.length > 1 ? 's' : ''}</Typography.Text>}
                                            key="1"
                                            style={{ padding: 0, border: 'none' }}
                                        >
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                                {sortedMilestones.map((m) => renderMilestoneRow(m, false))}
                                            </div>
                                        </Collapse.Panel>
                                    </Collapse>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    const renderChatPanel = (record: Lead) => {
        return (
            <div style={{ height: 'calc(100vh - 200px)', padding: 0 }}>
                <WhatsAppChat
                    logs={logs}
                    wabaName={record.wabaName || 'Bot'}
                    userName={record.username || 'User'}
                />
            </div>
        );
    };

    return (
        <CommonLayout>
            <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                    <Heading title="Leads" />
                    <Button type="primary" icon={<DownloadOutlined />} onClick={handleExport} className="bg-[#1F344C]">
                        Export Data
                    </Button>
                </div>

                <div className="bg-gray-50 p-4">
                    <Card className="mb-6 shadow-sm">
                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-7 gap-4">
                            {/* Account */}
                            <div>
                                <Typography.Text type="secondary" className="mb-1">Account</Typography.Text>
                                <Select
                                    placeholder="Select Account"
                                    value={filters.orgId}
                                    onChange={(val) => {
                                        setFilters(prev => ({ ...prev, orgId: val }));
                                        setPagination(prev => ({ ...prev, current: 1 }));
                                    }}
                                    className="w-full"
                                >
                                    {availableOrgs.map(org => (
                                        <Option key={org.orgId} value={org.orgId}>
                                            <Tooltip title={org.orgName}>{org.orgName}</Tooltip>
                                        </Option>
                                    ))}
                                </Select>
                            </div>

                            {/* Source */}
                            <div>
                                <Typography.Text type="secondary" className="mb-1">Source</Typography.Text>
                                <Select
                                    placeholder="Select Source"
                                    value={filters.source}
                                    onChange={(val) => {
                                        setFilters(prev => ({
                                            ...prev,
                                            source: val,
                                            campaignId: undefined,
                                            milestone: '',
                                            wabaId: undefined,
                                        }));
                                        setPagination(prev => ({ ...prev, current: 1 }));
                                    }}
                                    className="w-full"
                                >
                                    <Option value="Meta">Meta</Option>
                                    <Option value="organic">Organic</Option>
                                </Select>
                            </div>

                            {/* Campaign / WABA */}
                            {filters.source === 'Meta' ? (
                                <>
                                    <div className="xl:col-span-2">
                                        <Typography.Text type="secondary" className="mb-1">Campaign</Typography.Text>
                                        <Select
                                            placeholder="Select Campaign"
                                            value={filters.campaignId ?? undefined}
                                            onChange={(val) => {
                                                setFilters(prev => ({ ...prev, campaignId: val ?? undefined }));
                                                setPagination(prev => ({ ...prev, current: 1 }));
                                            }}
                                            className="w-full"
                                            allowClear
                                            loading={loadingCampaigns}
                                            showSearch
                                            filterOption={(input, option) => {
                                                const campaign = campaigns.find(c => c.id === option?.value);
                                                return (
                                                    campaign?.name.toLowerCase().includes(input.toLowerCase()) ||
                                                    String(campaign?.id).toLowerCase().includes(input.toLowerCase())
                                                ) ?? false;
                                            }}
                                            virtual={false}
                                        >
                                            {campaigns.map((c) => (
                                                <Option key={c.id} value={c.id}>
                                                    <Tooltip title={c.name}>{c.name}</Tooltip>
                                                </Option>
                                            ))}
                                        </Select>
                                    </div>
                                    <div>
                                        <Typography.Text type="secondary" className="mb-1">Milestone</Typography.Text>
                                        <Select
                                            placeholder="Select Milestone"
                                            value={filters.milestone || 'LeadSubmitted'}
                                            onChange={(val) => {
                                                setFilters(prev => ({ ...prev, milestone: val }));
                                                setPagination(prev => ({ ...prev, current: 1 }));
                                            }}
                                            className="w-full"
                                            disabled={!filters.campaignId}
                                        >
                                            <Option key="LeadSubmitted" value="LeadSubmitted">
                                                <Tooltip title="LeadSubmitted">LeadSubmitted</Tooltip>
                                            </Option>
                                            <Option key="All" value="All">
                                                <Tooltip title="All">All</Tooltip>
                                            </Option>
                                        </Select>
                                    </div>
                                </>
                            ) : (
                                <div>
                                    <Typography.Text type="secondary" className="mb-1">WABA</Typography.Text>
                                    <Select
                                        placeholder="Select WABA"
                                        value={filters.wabaId}
                                        onChange={(val) => setFilters(prev => ({ ...prev, wabaId: val }))}
                                        className="w-full"
                                        allowClear
                                        loading={loadingWaba}
                                        showSearch
                                        virtual={false}
                                        filterOption={(input, option) => {
                                            const waba = wabaAccounts.find(w => w.number === option?.value);
                                            return (
                                                waba?.name.toLowerCase().includes(input.toLowerCase()) ||
                                                String(waba?.number).toLowerCase().includes(input.toLowerCase())
                                            ) ?? false;
                                        }}
                                    >
                                        {wabaAccounts.map((w) => (
                                            <Option key={w.number} value={w.number}>
                                                <Tooltip title={`${w.name} (${w.number})`}>{w.name} ({w.number})</Tooltip>
                                            </Option>
                                        ))}
                                    </Select>
                                </div>
                            )}

                            {/* Date Range */}
                            <div>
                                <Typography.Text type="secondary" className="mb-1">Date Range</Typography.Text>
                                <RangePicker
                                    value={filters.dateRange}
                                    onChange={(dates) =>
                                        setFilters(prev => ({ ...prev, dateRange: dates || [null, null] }))
                                    }
                                    className="w-full"
                                />
                            </div>

                            {/* Submit */}
                            <div className="order-first justify-self-end md:col-start-2 xl:order-last xl:col-start-auto xl:justify-self-auto">
                                <Typography.Text className="mb-1 text-white/100">Submit</Typography.Text>
                                <Button
                                    type="primary"
                                    className="bg-[#1F344C] w-auto xl:w-full"
                                    onClick={fetchLeads}
                                    loading={loading}
                                    disabled={!filters.orgId || !filters.source}
                                >
                                    Apply Filters
                                </Button>
                            </div>
                        </div>
                    </Card>

                    <Table
                        columns={columns}
                        dataSource={data}
                        rowKey={(record) => `${record.userMobile}-${record.wabaNumber}`}
                        onRow={(record) => ({
                            onClick: () => openDrawer(record, 'vars'),
                            style: { cursor: 'pointer' },
                        })}
                        pagination={{
                            ...pagination,
                            onChange: (page, pageSize) =>
                                setPagination(prev => ({ ...prev, current: page, pageSize })),
                            showTotal: (total) => `Total ${total} leads`,
                            showSizeChanger: false,
                        }}
                        loading={loading}
                        scroll={{ x: true }}
                    />
                </div>
            </div>

            {/* Left Drawer */}
            <Drawer
                title={
                    drawerRecord ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <span style={{ fontWeight: 700, color: '#1F344C' }}>
                                {drawerRecord.username || drawerRecord.userMobile || 'Lead Details'}
                            </span>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
                                {drawerRecord.userMobile && (
                                    <span style={{ fontSize: 12, fontWeight: 400, color: '#888' }}>
                                        {drawerRecord.userMobile}
                                        {drawerRecord.city ? ` · ${drawerRecord.city}` : ''}
                                    </span>
                                )}
                                {drawerRecord.capiPushed && (
                                    <div style={{ display: 'flex', gap: 4 }}>
                                        {drawerRecord.capiPushed.split(',').map(tag => (
                                            <Tag key={tag.trim()} color="success" style={{ fontSize: '10px', margin: 0, padding: '0 4px', lineHeight: '16px', height: '18px' }}>
                                                CAPI: {tag.trim()}
                                            </Tag>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    ) : 'Lead Details'
                }
                placement="right"
                width={480}
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                destroyOnClose
            >
                {drawerRecord && (
                    <Tabs
                        activeKey={drawerTab}
                        onChange={(key) => setDrawerTab(key as 'vars' | 'milestones' | 'chat')}
                        type="card"
                        items={[
                            {
                                key: 'vars',
                                label: (
                                    <span>
                                        <AppstoreOutlined /> Variables&nbsp;
                                        {/* <Tag>{Object.keys(drawerRecord.var || {}).length}</Tag> */}
                                    </span>
                                ),
                                children: renderVarsPanel(drawerRecord),
                            },
                            {
                                key: 'milestones',
                                label: (
                                    <span>
                                        <ClockCircleOutlined /> Milestones&nbsp;
                                        <Tag>{Object.values(drawerRecord.milestones || {}).flat().length}</Tag>
                                    </span>
                                ),
                                children: renderMilestonesPanel(drawerRecord),
                            },
                            {
                                key: 'chat',
                                label: (
                                    <span>
                                        <MessageOutlined /> Chat
                                    </span>
                                ),
                                children: renderChatPanel(drawerRecord),
                            },
                        ]}
                    />
                )}
            </Drawer>
        </CommonLayout>
    );
};

export default Leads;
